/**
 * Per-claim breakdown builder for the MDF "Per-Claim Breakdown" detail-page
 * section (multi-claim fund requests).
 *
 * On a multi-claim MDF FR the agent validates EACH submitted claim on its OWN
 * receipts (Steps 3/4/5/6) and the FR outcome is the worst claim. This joins
 * every per-claim step's `per_claim_results` on `fund_claim_id` into one row per
 * claim so the reviewer can see each claim's amount, converted invoice total,
 * cost-share %, its own recommendation, and WHICH step drove that outcome.
 *
 * Step 5 (cost-share) is the SOLE source of every financial field on a claim
 * card (amount, currency, FX, invoice_breakdown). When step 5 recorded NO
 * per-claim rows it deliberately refused to allocate (a shared FR-level receipt
 * backing >1 claim, or an unattributed receipt) and escalated for a human —
 * steps 3/4/6 still fan out and populate their per_claim_results, so keying off
 * them would render cards with blank amounts and a false "No invoices
 * attributed to this claim". So: no step-5 per-claim rows -> return [] (the
 * FR-level aggregate boxes stand).
 */

// Steps that run per-claim, in display / tie-break order. Step 5 seeds each
// row; the others contribute their per-claim verdict + drive attribution.
export const MDF_PER_CLAIM_STEPS = [
  'step_3_invoice_present',
  'step_4_entity_linkage',
  'step_5_cost_share',
  'step_6_line_item_eligibility',
];

// Human labels for the "driven by: <step>" attribution line.
export const MDF_STEP_LABELS = {
  step_3_invoice_present: 'invoice present',
  step_4_entity_linkage: 'entity linkage',
  step_5_cost_share: 'cost-share',
  step_6_line_item_eligibility: 'line-item eligibility',
};

// Severity rank mirroring the agent's per_claim.worst_of precedence
// (reject > escalate > pass). An unknown/missing verdict defaults to escalate —
// NEVER a silent pass — matching per_claim.worst_of's _SEVERITY.get(s, escalate).
function rank(status) {
  const v = (status || '').toLowerCase();
  if (v === 'reject') return 3;
  if (v === 'escalate' || v === 'needs_review') return 2;
  if (v === 'pass') return 1;
  return 2; // unknown -> escalate, never pass
}

/**
 * Build the per-claim breakdown rows from the parsed per-check results map.
 * @param {Object} checks - map of step key -> { status, per_claim_results: [...] }
 * @returns {Array<Object>} one row per claim (empty when not a multi-claim run
 *   or when step 5 recorded no per-claim rows). Each row:
 *   { fund_claim_id, label, recommendation, driving_step_label, claim_amount,
 *     claim_currency, invoice_total, invoice_total_converted, invoice_currency,
 *     cost_share_pct, cost_share_status, max_percent, fx_applied,
 *     invoice_breakdown }
 */
export function buildMdfPerClaimBreakdown(checks) {
  const c = checks || {};
  const s5rows = (c.step_5_cost_share || {}).per_claim_results;
  if (!Array.isArray(s5rows) || !s5rows.length) return [];

  const byClaim = {};
  const order = [];
  MDF_PER_CLAIM_STEPS.forEach((stepKey) => {
    const rows = (c[stepKey] || {}).per_claim_results;
    if (!Array.isArray(rows)) return;
    rows.forEach((row) => {
      const cid = row.fund_claim_id;
      if (!cid) return;
      if (!byClaim[cid]) {
        byClaim[cid] = { fund_claim_id: cid, byStep: {}, step5: null };
        order.push(cid);
      }
      if (row.status) byClaim[cid].byStep[stepKey] = row.status;
      if (stepKey === 'step_5_cost_share') byClaim[cid].step5 = row;
    });
  });

  // Step 5 is the sole source of every financial field on a card. Skip any claim
  // that appears in steps 3/4/6 but is MISSING from step 5's per_claim_results (a
  // partial mismatch, not an all-empty step 5) — otherwise its card renders blank
  // amounts and the false "No invoices attributed to this claim" the whole-empty
  // guard above prevents for the all-empty case (AutoSDE).
  return order
    .filter((cid) => byClaim[cid].step5)
    .map((cid) => {
    const entry = byClaim[cid];
    const s5 = entry.step5 || {};
    const m = String(cid).match(/claim-\w+$/i);
    // Overall recommendation + which step drove it (highest-severity step; ties
    // break on step order). 'pass' has no driving step (nothing to attribute).
    let recommendation = 'pass';
    let drivingStep = null;
    MDF_PER_CLAIM_STEPS.forEach((stepKey) => {
      const st = entry.byStep[stepKey];
      if (st === undefined) return;
      if (rank(st) > rank(recommendation)) {
        // Normalize to the canonical verdict at this severity: rank 3 -> reject,
        // rank 2 -> escalate (covers needs_review AND any unknown status, which
        // must never silently pass), rank 1 -> pass.
        const r = rank(st);
        recommendation = r === 3 ? 'reject' : (r === 2 ? 'escalate' : 'pass');
        drivingStep = stepKey;
      }
    });
    return {
      fund_claim_id: cid,
      label: m ? m[0] : cid,
      recommendation,
      driving_step_label: recommendation === 'pass' ? null : (MDF_STEP_LABELS[drivingStep] || null),
      claim_amount: s5.claim_amount,
      claim_currency: s5.claim_currency,
      invoice_total: s5.invoice_total,
      invoice_total_converted: s5.invoice_total_converted,
      invoice_currency: s5.invoice_currency,
      cost_share_pct: s5.cost_share_pct,
      // Step-5's OWN verdict — the cost-share indicator must reflect cost-share
      // specifically, NOT the claim's overall recommendation (worst-of all
      // steps), so it agrees with the FR-level Cost-Share box (step5.status).
      cost_share_status: s5.status,
      max_percent: s5.max_percent ?? 50,
      fx_applied: s5.fx_applied || null,
      invoice_breakdown: Array.isArray(s5.invoice_breakdown) ? s5.invoice_breakdown : [],
    };
  });
}
